import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled, { createGlobalStyle, keyframes } from "styled-components";

import { useWindowDimensions } from "app/hooks";
import { useAgentMediaList } from "../../hooks/DigitalSignage/GetAgentMediaList";
import { useUserInfo } from "../../../HomeTown/hooks/Auth/GetUserInfo";
import { FullImageList } from "../FullImageList";
import { Two } from "./Two";
import { Six } from "./Six";

/* =========================
 * THEME (Bright & Readable)
 * ========================= */
const PRIMARY_HEX = "#3A77F2";
const ACCENT_HEX  = "#58B2FF";
const BG_1 = "#F5F8FE";
const BG_2 = "#ECF3FF";
const TEXT_MAIN = "#0B1B2E";
const TEXT_SUB  = "#2C4159";
const CARD_BG   = "rgba(255,255,255,0.96)";
const CARD_BORDER = "#E6ECF5";
const SHADOW = "0 12px 40px rgba(18, 45, 90, 0.12)";

type SignageTheme = {
  primary: string;
  accent: string;
  textMain: string;
  textSub: string;
  stageGradient: string;
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  tickerColor: string;
  tickerBg: string;
  fallbackBg: string;
  fallbackColor: string;
  fallbackBorder: string;
  mediaBg: string;
  priceBg: string;
  priceColor: string;
  priceShadow: string;
};

const DAY_THEME: SignageTheme = {
  primary: PRIMARY_HEX,
  accent: ACCENT_HEX,
  textMain: TEXT_MAIN,
  textSub: TEXT_SUB,
  stageGradient: `
    radial-gradient(1200px 800px at 15% 10%, ${BG_2} 0%, transparent 60%),
    radial-gradient(1000px 800px at 80% 20%, ${PRIMARY_HEX}18 0%, transparent 55%),
    ${BG_1}
  `,
  cardBg: CARD_BG,
  cardBorder: CARD_BORDER,
  cardShadow: SHADOW,
  tickerColor: TEXT_MAIN,
  tickerBg: "rgba(255,255,255,0.65)",
  fallbackBg: "#f5f8fe",
  fallbackColor: "#2c4159",
  fallbackBorder: "#e6ecf5",
  mediaBg: "#ffffff",
  priceBg: "#FACC15",
  priceColor: "#0b1b2e",
  priceShadow: "none",
};

const NIGHT_THEME: SignageTheme = {
  primary: "#2563EB",
  accent: "#38BDF8",
  textMain: "#E2E8F0",
  textSub: "#CBD5F5",
  stageGradient: `#000000`,
  cardBg: "#111827",
  cardBorder: "#111827",
  cardShadow: "0 12px 40px rgba(2,6,23,0.65)",
  tickerColor: "#F8FAFC",
  tickerBg: "rgba(2,6,23,0.85)",
  fallbackBg: "#0f172a",
  fallbackColor: "#e2e8f0",
  fallbackBorder: "rgba(51,65,85,0.9)",
  mediaBg: "#111827",
  priceBg: "#111827",
  priceColor: "#FFE45A",
  priceShadow: "0 0 12px #FFE45A, 0 0 24px #FFD700",
};

/* =========================
 * 데이터 타입 & 유틸
 * ========================= */
type MediaKind = 'sale' | 'external' | 'manager' | 'self';

type MediaItemResponse = {
  adId?: number | null;
  kind?: MediaKind;
  imageUrlList?: unknown;
  saleInfo?: unknown;
  sec?: number | string | null;
  firstSec?: number | string | null;
  isDefault?: boolean | null;
  order?: number | string | null;
  sourceType?: number | string | null;
};

type MediaSaleBlock = {
  maxCount?: number | string | null;
  items?: MediaItemResponse[] | null;
  self?: MediaItemResponse[] | null;
};

type MediaExternalBlock = {
  maxCount?: number | string | null;
  items?: MediaItemResponse[] | null;
};

type MediaManagerBlock = {
  items?: MediaItemResponse[] | null;
  saleItems?: MediaItemResponse[] | null;
  adItems?: MediaItemResponse[] | null;
};

type MediaSettingsBlock = {
  perAdDurationSec?: number | string | null;
};

type MediaPayload = {
  sale?: MediaSaleBlock;
  external?: MediaExternalBlock;
  manager?: MediaManagerBlock;
  perAdDurationSec?: number | string | null;
  settings?: MediaSettingsBlock | null;
};

type NormalizedItem = {
  adId?: number | null;
  imageUrlList: string[];
  saleInfo: Record<string, any>;
  sec: number;
  firstSec?: number;
  originalKind?: MediaKind;
  order?: number;
  sourceType?: number;
};

type PreparedManagerItems = {
  all: NormalizedItem[];
  sale: NormalizedItem[];
  ad: NormalizedItem[];
};
type Slide = {
  kind: MediaKind;
  layout: 1 | 2 | 6;
  items: NormalizedItem[];
  dwellMs: number;
};

const DEFAULT_SEC = 15;
const MIN_DWELL_MS = 1000;
const FULL_LAYOUT_PRIMARY_DWELL_MS = 15000;
const AD_TYPE_AD = 1;
const AD_TYPE_SALE = 2;
const clampFontScale = (value: number): number => {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(value, 0.45), 1.1);
};
const WINDOW_SIZE = 6;

const isDaytime = (date: Date): boolean => {
  const hour = date.getHours();
  if (hour >= 7 && hour <= 17) return true;
  return false;
};

const toPositiveNumber = (value: any, fallback: number): number => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  return Math.floor(num);
};

const toOptionalNumber = (value: any): number | undefined => {
  const num = Number(value);
  if (!Number.isFinite(num)) return undefined;
  return num;
};

const parseJson = <T,>(value: any, fallback: T): T => {
  if (value == null) return fallback;

  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch (e) {
      return fallback;
    }
  }

  if (typeof value === 'object') {
    if (value instanceof ArrayBuffer) {
      try {
        const decoded = new TextDecoder().decode(new Uint8Array(value as ArrayBuffer));
        return JSON.parse(decoded) as T;
      } catch (e) {
        return fallback;
      }
    }

    if (value instanceof Uint8Array) {
      try {
        const decoded = new TextDecoder().decode(value);
        return JSON.parse(decoded) as T;
      } catch (e) {
        return fallback;
      }
    }

    // Buffer (Node) 지원
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((value as any)?.type === 'Buffer' && Array.isArray((value as any)?.data)) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const arr = (value as any).data as number[];
        const decoded = new TextDecoder().decode(new Uint8Array(arr));
        return JSON.parse(decoded) as T;
      } catch (e) {
        return fallback;
      }
    }

    return value as T;
  }

  return fallback;
};

const normalizeImageList = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.filter((url) => typeof url === 'string' && url.trim().length > 0);
  }

  if (typeof value === 'string' || typeof value === 'object') {
    const parsed = parseJson<string[]>(value, []);
    return Array.isArray(parsed)
      ? parsed.filter((url) => typeof url === 'string' && url.trim().length > 0)
      : [];
  }

  return [];
};

const hasRenderableContent = (
  item: MediaItemResponse | null | undefined
): item is MediaItemResponse => {
  if (!item) return false;
  const urls = normalizeImageList(item.imageUrlList);
  if (urls.length > 0) return true;

  const saleInfo = parseJson<Record<string, unknown>>(item.saleInfo, {});
  if (!saleInfo) return false;

  return Object.values(saleInfo).some(
    (value) => typeof value === "string" && value.trim().length > 0
  );
};

const toOptionalPositive = (value: unknown): number | undefined => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return undefined;
  return Math.floor(num);
};

const sanitizeSaleInfo = (info: Record<string, unknown>): Record<string, any> => {
  return Object.entries(info).reduce<Record<string, any>>((acc, [key, value]) => {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) acc[key] = trimmed;
    } else if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const normalizeItem = (
  item: MediaItemResponse,
  fallbackKind: MediaKind,
  perAdDurationSec?: number
): NormalizedItem => {
  const imageUrlList = normalizeImageList(item?.imageUrlList);
  const saleInfoRaw = parseJson<Record<string, unknown>>(item?.saleInfo, {});
  const saleInfo = sanitizeSaleInfo(saleInfoRaw);

  const resolvedKind = item?.kind ?? fallbackKind;
  const fallbackSec = perAdDurationSec ?? DEFAULT_SEC;
  const sec = perAdDurationSec ?? toPositiveNumber(item?.sec, fallbackSec);
  const firstSecSource =
    perAdDurationSec ?? (item?.firstSec != null ? item.firstSec : undefined);
  const firstSec = firstSecSource
    ? toPositiveNumber(firstSecSource, sec)
    : sec;
  const orderValue =
    toOptionalNumber(item?.order) ??
    toOptionalNumber((saleInfo as any)?.order) ??
    toOptionalNumber((saleInfo as any)?.sortOrder) ??
    toOptionalNumber((saleInfo as any)?.displayOrder);
  const sourceType = toOptionalNumber(item?.sourceType);

  return {
    adId: item?.adId ?? null,
    imageUrlList,
    saleInfo,
    sec: Math.max(sec, 1),
    firstSec: Math.max(firstSec ?? sec, 1),
    originalKind: resolvedKind,
    order: orderValue,
    sourceType,
  };
};

const sortByOrder = (items: NormalizedItem[]): NormalizedItem[] => {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const orderA = Number.isFinite(a.item.order) ? (a.item.order as number) : Number.MAX_SAFE_INTEGER;
      const orderB = Number.isFinite(b.item.order) ? (b.item.order as number) : Number.MAX_SAFE_INTEGER;
      if (orderA === orderB) {
        return a.index - b.index;
      }
      return orderA - orderB;
    })
    .map(({ item }) => item);
};

const cloneNormalizedItem = (item: NormalizedItem): NormalizedItem => ({
  ...item,
  imageUrlList: [...item.imageUrlList],
  saleInfo: { ...(item.saleInfo ?? {}) },
});


const prepareSaleItems = (
  block: MediaSaleBlock | undefined,
  perAdDurationSec?: number
): NormalizedItem[] => {
  if (!block) return [];

  const saleItemsRaw = Array.isArray(block.items) ? block.items : [];
  const saleItems = saleItemsRaw
    .filter(hasRenderableContent)
    .map((item) => normalizeItem(item, "sale", perAdDurationSec));

  const selfRaw = Array.isArray(block.self)
    ? block.self
    : block.self
      ? [block.self]
      : [];
  const selfItems = selfRaw
    .filter(hasRenderableContent)
    .map((item) => normalizeItem(item, "self", perAdDurationSec));

  const maxCountRaw =
    block.maxCount != null ? toPositiveNumber(block.maxCount, 0) : 0;
  const totalDesired =
    maxCountRaw > 0 ? maxCountRaw : saleItems.length + selfItems.length;

  const combined = sortByOrder([...saleItems, ...selfItems]);

  if (totalDesired > 0) {
    return combined.slice(0, totalDesired);
  }

  return combined;
};

const prepareExternalItems = (
  block: MediaExternalBlock | undefined,
  perAdDurationSec?: number
): NormalizedItem[] => {
  if (!block) return [];

  const itemsRaw = Array.isArray(block.items) ? block.items : [];
  const normalized = sortByOrder(
    itemsRaw
    .filter(hasRenderableContent)
    .map((item) => normalizeItem(item, "external", perAdDurationSec))
  );

  const maxCountRaw =
    block.maxCount != null ? toPositiveNumber(block.maxCount, 0) : 0;
  const limit = maxCountRaw > 0 ? maxCountRaw : normalized.length;
  return normalized.slice(0, limit);
};

const prepareManagerItems = (
  block: MediaManagerBlock | undefined,
  perAdDurationSec?: number
): PreparedManagerItems => {
  const normalizeList = (list?: MediaItemResponse[] | null) => {
    const raw = Array.isArray(list) ? list : [];
    return sortByAdIdDesc(
      raw
        .filter(hasRenderableContent)
        .map((item) => normalizeItem(item, "manager", perAdDurationSec))
    );
  };

const sortByAdIdDesc = (items: NormalizedItem[]): NormalizedItem[] => {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const adA = Number.isFinite(a.item.adId as number) ? (a.item.adId as number) : -Infinity;
      const adB = Number.isFinite(b.item.adId as number) ? (b.item.adId as number) : -Infinity;
      if (adA === adB) {
        return b.index - a.index;
      }
      return adB - adA;
    })
    .map(({ item }) => item);
};

  const all = normalizeList(block?.items).slice(0, 6);
  const saleFromBlock = normalizeList(block?.saleItems);
  const adFromBlock = normalizeList(block?.adItems);

  const sale =
    saleFromBlock.length > 0
      ? saleFromBlock.slice(0, 6)
      : all.filter((item) => item.sourceType === AD_TYPE_SALE);
  const ad =
    adFromBlock.length > 0
      ? adFromBlock.slice(0, 6)
      : all.filter((item) => item.sourceType === AD_TYPE_AD);

  return {
    all,
    sale,
    ad,
  };
};

const buildPlaybackSlides = (payload?: MediaPayload): Slide[] => {
  if (!payload) return [];

  const configuredDuration =
    toOptionalPositive(
      payload.perAdDurationSec ?? payload.settings?.perAdDurationSec
    ) ?? undefined;

  const salePool = prepareSaleItems(payload.sale, configuredDuration);
  const managerPrepared = prepareManagerItems(payload.manager, configuredDuration);
  const managerItems = managerPrepared.all;
  const externalItems = prepareExternalItems(payload.external, configuredDuration);

  const saleFallbackSource = managerPrepared.sale;
  const adFallbackSource = managerPrepared.ad;

  const saleWindows = createWindowSets(salePool, saleFallbackSource, WINDOW_SIZE);
  const advWindows =
    externalItems.length > 0
      ? createWindowSets(externalItems, adFallbackSource, WINDOW_SIZE)
      : [];

  const saleSetSlides = saleWindows.map((window) =>
    buildWindowCycles(window, 'sale')
  );
  const advSetSlides = advWindows.map((window) =>
    buildWindowCycles(window, 'external')
  );

  const playlistGroups: Slide[][] = [];

  if (saleSetSlides.length && advSetSlides.length) {
    // 매물/외부 세트를 번갈아 노출하되 매물 세트가 부족하면 앞 세트를 재사용해 외부 세트 사이에 끼워 넣는다.
    const cycleCount = Math.max(saleSetSlides.length, advSetSlides.length);
    for (let i = 0; i < cycleCount; i += 1) {
      const saleGroup = saleSetSlides[i % saleSetSlides.length];
      if (saleGroup) {
        playlistGroups.push(saleGroup);
      }
      if (i < advSetSlides.length) {
        playlistGroups.push(advSetSlides[i]);
      }
    }
  } else if (saleSetSlides.length) {
    playlistGroups.push(...saleSetSlides);
  } else if (advSetSlides.length) {
    playlistGroups.push(...advSetSlides);
  } else if (managerItems.length) {
    const managerWindows = createWindowSets(managerItems, [], WINDOW_SIZE);
    playlistGroups.push(
      ...managerWindows.map((window) => buildWindowCycles(window, 'manager'))
    );
  }

  return playlistGroups.flat().filter((slide) => slide.items.length > 0);
};

const createWindowSets = (
  pool: NormalizedItem[],
  fallback: NormalizedItem[],
  windowSize: number
): NormalizedItem[][] => {
  const sets: NormalizedItem[][] = [];
  const orderValue = (value: number | undefined): number => {
    return Number.isFinite(value) ? (value as number) : Number.MAX_SAFE_INTEGER;
  };
  const sortByOrder = (list: NormalizedItem[]): NormalizedItem[] => {
    return list.sort((a, b) => orderValue(a.order) - orderValue(b.order));
  };

  const fallbackClone = fallback.map((item) => cloneNormalizedItem(item));
  let fallbackIndex = 0;

  const takeFallback = (): NormalizedItem | null => {
    if (fallbackClone.length === 0) return null;
    const item = cloneNormalizedItem(fallbackClone[fallbackIndex % fallbackClone.length]);
    fallbackIndex += 1;
    return item;
  };

  const poolClone = sortByOrder(pool.map((item) => cloneNormalizedItem(item)));

  if (poolClone.length === 0 && fallbackClone.length === 0) {
    return [];
  }

  if (poolClone.length === 0) {
    const window: NormalizedItem[] = [];
    for (let i = 0; i < windowSize; i += 1) {
      const fallbackItem = takeFallback();
      if (fallbackItem) {
        window.push(fallbackItem);
      }
    }
    if (window.length) {
      while (window.length < windowSize) {
        window.push(cloneNormalizedItem(window[window.length % window.length]));
      }
      sets.push(window);
    }
    return sets;
  }

  for (let start = 0; start < poolClone.length; start += windowSize) {
    const window = poolClone.slice(start, start + windowSize);
    while (window.length < windowSize) {
      const fallbackItem = takeFallback();
      if (fallbackItem) {
        window.push(fallbackItem);
      } else {
        break;
      }
    }
    if (window.length < windowSize && window.length > 0) {
      const seed = window.slice();
      while (window.length < windowSize) {
        window.push(cloneNormalizedItem(seed[window.length % seed.length]));
      }
    }
    if (window.length === windowSize) {
      sets.push(window);
    }
  }

  return sets;
};

const buildWindowCycles = (windowItems: NormalizedItem[], kind: MediaKind): Slide[] => {
  if (windowItems.length === 0) return [];
  const slides: Slide[] = [];
  const size = windowItems.length;

  for (let cycle = 0; cycle < size; cycle += 1) {
    const rotated: NormalizedItem[] = [];
    for (let offset = 0; offset < size; offset += 1) {
      const idx = (cycle + offset) % size;
      rotated.push(cloneNormalizedItem(windowItems[idx]));
    }

    const primary = cloneNormalizedItem(rotated[0]);
    const baseMs = Math.max(
      ((primary.firstSec ?? primary.sec) || DEFAULT_SEC) * 1000,
      MIN_DWELL_MS
    );
    const dwellLayout1 =
      kind === 'sale' || kind === 'self'
        ? Math.max(FULL_LAYOUT_PRIMARY_DWELL_MS, baseMs)
        : baseMs;
    const dwellOthers = Math.max(
      DEFAULT_SEC * 1000,
      MIN_DWELL_MS
    );

    slides.push({
      kind,
      layout: 1,
      items: [primary],
      dwellMs: dwellLayout1,
    });
    slides.push({
      kind,
      layout: 2,
      items: rotated.map((item) => cloneNormalizedItem(item)),
      dwellMs: dwellOthers,
    });
    slides.push({
      kind,
      layout: 6,
      items: rotated.map((item) => cloneNormalizedItem(item)),
      dwellMs: dwellOthers,
    });
  }

  return slides;
};

const renderSlideContent = (
  slide: Slide | undefined,
  slideIndex: number,
  fontScale: number,
  layerKey: string,
  isActive: boolean,
): React.ReactNode => {
  if (!slide) return null;
  const { layout, items, kind } = slide;
  const baseKey = `${kind}-${layout}-${slideIndex}-${layerKey}`;
  const zIndex = layerKey === 'next' ? 2 : 1;

  if (layout === 1) {
    const it = items[0];
    return (
      <SlideLayer key={baseKey} $visible={isActive} $zIndex={zIndex}>
        <ContentWrapper>
          <FullBox>
            <FullImageList
              key={`${baseKey}-full`}
              imagelist={it?.imageUrlList ?? []}
              isWidth
              setItem={items}
              type="full"
              num={0}
              setIntervalTime={() => {}}
              globalScale={fontScale}
              isActive={isActive}
              deferUntilActive
            />
          </FullBox>
        </ContentWrapper>
      </SlideLayer>
    );
  }

  if (layout === 2) {
    const ensureIndices = (start: number, count: number): number[] => {
      if (items.length === 0) return [];
      return Array.from({ length: count }, (_, offset) => {
        const target = start + offset;
        if (items[target]) return target;
        const safeLength = items.length;
        return safeLength === 0 ? 0 : ((target % safeLength) + safeLength) % safeLength;
      });
    };

    const topCount = items.length === 0 ? 0 : 2;
    const topIndices = ensureIndices(0, topCount);
    const bottomIndices = ensureIndices(2, items.length === 0 ? 0 : 4);

    return (
      <SlideLayer key={baseKey} $visible={isActive} $zIndex={zIndex}>
        <ContentWrapper>
          <TwoLayout>
            <TwoTopRow>
              {topIndices.map((itemIndex, topSlot) => {
                const candidate = items[itemIndex];
                const hasSaleType = Boolean(candidate?.saleInfo?.saleType);
                return (
                  <TwoTopCard key={`${baseKey}-top-${topSlot}`}>
                    {hasSaleType ? (
                      <Two item={items} num={itemIndex} type="top" fontScale={fontScale} isActive={isActive} />
                    ) : (
                      <FullImageList
                        key={`${baseKey}-two-${itemIndex}`}
                        imagelist={candidate?.imageUrlList ?? []}
                        isWidth
                        setItem={items}
                        type="two"
                        num={itemIndex}
                        setIntervalTime={() => {}}
                        globalScale={fontScale}
                        isActive={isActive}
                        deferUntilActive
                      />
                    )}
                  </TwoTopCard>
                );
              })}
            </TwoTopRow>
            {bottomIndices.length > 0 && (
              <TwoBottomRow>
                {bottomIndices.map((itemIndex, bottomSlot) => (
                  <TwoBottomCard key={`${baseKey}-bottom-${bottomSlot}`}>
                    <Six item={items} num={itemIndex} fontScale={fontScale} isActive={isActive} />
                  </TwoBottomCard>
                ))}
              </TwoBottomRow>
            )}
          </TwoLayout>
        </ContentWrapper>
      </SlideLayer>
    );
  }

  if (layout === 6) {
    return (
      <SlideLayer key={baseKey} $visible={isActive} $zIndex={zIndex}>
        <ContentWrapper>
          <SixWrap>
            <SixRow>
              {[0, 1, 2].map((itemIndex) => (
                <SixCard key={`${baseKey}-six-top-${itemIndex}`}>
                  <Six item={items} num={itemIndex} fontScale={fontScale} isActive={isActive} />
                </SixCard>
              ))}
            </SixRow>
            <SixRow>
              {[3, 4, 5].map((itemIndex) => (
                <SixCard key={`${baseKey}-six-bottom-${itemIndex}`}>
                  <Six item={items} num={itemIndex} fontScale={fontScale} isActive={isActive} />
                </SixCard>
              ))}
            </SixRow>
          </SixWrap>
        </ContentWrapper>
      </SlideLayer>
    );
  }

  return (
    <SlideLayer key={`${baseKey}-empty`} $visible={isActive} $zIndex={zIndex}>
      <ContentWrapper />
    </SlideLayer>
  );
};

/* =========================
 * GlobalStyle (라이트 배경)
 * ========================= */
const GlobalStyle = createGlobalStyle`
  :root {
    --primary: ${PRIMARY_HEX};
    --accent: ${ACCENT_HEX};
    --stage-gradient: ${DAY_THEME.stageGradient};
    --text-main: ${TEXT_MAIN};
    --text-sub: ${TEXT_SUB};
    --card-bg: ${CARD_BG};
    --card-border: ${CARD_BORDER};
    --card-shadow: ${SHADOW};
    --ticker-color: ${TEXT_MAIN};
    --ticker-bg: rgba(255,255,255,0.65);
    --fallback-bg: #f5f8fe;
    --fallback-color: #2c4159;
    --fallback-border: #e6ecf5;
    --media-bg: ${DAY_THEME.mediaBg};
    --price-bg: ${DAY_THEME.priceBg};
    --price-color: ${DAY_THEME.priceColor};
    --price-shadow: ${DAY_THEME.priceShadow};
  }
  html, body, #__next {
    width: 100%;
    height: 100%;
    background: var(--stage-gradient, ${DAY_THEME.stageGradient});
    color: var(--text-main, ${TEXT_MAIN});
    overscroll-behavior: none;
  }
  body { margin: 0; padding: 0; overflow: hidden; }
  * { box-sizing: border-box; }
`;

/* =========================
 * Animations
 * ========================= */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const marquee = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;
/* =========================
 * 메인 컴포넌트 (FULLSCREEN CONTAIN)
 * ========================= */
export const DigitalSignage: React.FC = () => {
  const { width, height } = useWindowDimensions();

  // CONTAIN: 16:9 유지 + 크롭 없음 (여백은 배경으로)
  const BASE_W = 1920;
  const BASE_H = 1080;
  const widthRatio = width > 0 ? width / BASE_W : 1;
  const heightRatio = height > 0 ? height / BASE_H : 1;
  const fontScale = clampFontScale(Math.min(widthRatio, heightRatio));

  const { agentMediaList, mutateAgentMediaList } = useAgentMediaList();


  const { userInfo } = useUserInfo();

  const slides = useMemo<Slide[]>(
    () => buildPlaybackSlides(agentMediaList as MediaPayload | undefined),
    [agentMediaList],
  );

  // 슬릭 컨트롤러와 인터벌 관리
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [targetIndex, setTargetIndex] = useState(0);
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshingRef = useRef(false);

  const clearTimers = () => {
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = null;
    }
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  };

  const refreshSlides = useCallback(async () => {
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    try {
      await mutateAgentMediaList();
    } catch (error) {
      console.error('Failed to refresh signage media:', error);
    } finally {
      refreshingRef.current = false;
    }
  }, [mutateAgentMediaList]);

  const scheduleNext = useCallback((fromIndex: number) => {
    clearTimers();
    if (!slides.length) return;

    const nextIndex = (fromIndex + 1) % slides.length;
    const dwell = slides[fromIndex]?.dwellMs || DEFAULT_SEC * 1000;

    dwellTimerRef.current = setTimeout(() => {
      setTargetIndex(nextIndex);
      setIsTransitioning(true);
      transitionTimerRef.current = setTimeout(() => {
        setCurrentIndex(nextIndex);
        setIsTransitioning(false);
        if (nextIndex === 0) {
          refreshSlides();
        }
        scheduleNext(nextIndex);
      }, 600);
    }, dwell);
  }, [slides, refreshSlides]);

  useEffect(() => {
    setCurrentIndex(0);
    setTargetIndex(0);
    setIsTransitioning(false);
    scheduleNext(0);
    return () => clearTimers();
  }, [slides, scheduleNext]);

  const handleKeydown = useCallback((event: KeyboardEvent) => {
    if (!slides.length) return;
    if (event.key === 'ArrowRight' || event.key === 'Right') {
      clearTimers();
      const nextIndex = (currentIndex + 1) % slides.length;
      setTargetIndex(nextIndex);
      setIsTransitioning(true);
      transitionTimerRef.current = setTimeout(() => {
        setCurrentIndex(nextIndex);
        setIsTransitioning(false);
        if (nextIndex === 0) refreshSlides();
        scheduleNext(nextIndex);
      }, 10);
    } else if (event.key === 'ArrowLeft' || event.key === 'Left') {
      clearTimers();
      const prevIndex = (currentIndex - 1 + slides.length) % slides.length;
      setTargetIndex(prevIndex);
      setIsTransitioning(true);
      transitionTimerRef.current = setTimeout(() => {
        setCurrentIndex(prevIndex);
        setIsTransitioning(false);
        scheduleNext(prevIndex);
      }, 10);
    }
  }, [slides, currentIndex, scheduleNext, refreshSlides]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  const now = useNow(1000);
  const isDayMode = isDaytime(now);
  const themeVars = isDayMode ? DAY_THEME : NIGHT_THEME;
  const stageStyle = useMemo<React.CSSProperties>(() => {
    const cssVars: Record<string, string> = {
      '--primary': themeVars.primary,
      '--accent': themeVars.accent,
      '--stage-gradient': themeVars.stageGradient,
      '--text-main': themeVars.textMain,
      '--text-sub': themeVars.textSub,
      '--card-bg': themeVars.cardBg,
      '--card-border': themeVars.cardBorder,
      '--card-shadow': themeVars.cardShadow,
      '--ticker-color': themeVars.tickerColor,
      '--ticker-bg': themeVars.tickerBg,
      '--fallback-bg': themeVars.fallbackBg,
      '--fallback-color': themeVars.fallbackColor,
      '--fallback-border': themeVars.fallbackBorder,
      '--media-bg': themeVars.mediaBg,
      '--price-bg': themeVars.priceBg,
      '--price-color': themeVars.priceColor,
      '--price-shadow': themeVars.priceShadow,
    };
    return {
      width: `100vw`,
      height: `100vh`,
      transform: `translate(-50%, -50%)`,
      ...cssVars,
    };
  }, [themeVars]);

  return (
      <Viewport>
        <Helmet><style>{`body{margin:0;padding:0;overflow:hidden;}`}</style></Helmet>
        <GlobalStyle />

        {/* 1920x1080 기준 캔버스: 컨테인 스케일로 중앙 배치 */}
        <Stage style={stageStyle}>
          {/* 상단 정보바 */}
          <TopHUD>
            <Brand>
              <BrandName>{userInfo?.store || "Your Store"}</BrandName>
            </Brand>
            <Clock>
              <ClockDate>{formatDate(now)}</ClockDate>
              <ClockTime>{formatTime(now)}</ClockTime>
            </Clock>
          </TopHUD>


          {/* 메인 콘텐츠 카드 */}
          <ContentCard>
            <SlideStage>
              {renderSlideContent(slides[currentIndex], currentIndex, fontScale, 'current', !isTransitioning)}
              {isTransitioning && renderSlideContent(slides[targetIndex], targetIndex, fontScale, 'next', true)}
              {!slides.length && (
                <SlideLayer $visible $zIndex={1}>
                  <EmptySlide>표시할 콘텐츠가 없습니다.</EmptySlide>
                </SlideLayer>
              )}
            </SlideStage>
          </ContentCard>

          {/* 하단 티커 */}
          <BottomDock>
            <Ticker>
              <TickerTrack>
              <span>
                {userInfo?.store || "Your Store"} · {userInfo?.address || ""} ·{" "}
                {userInfo?.detailAddress || ""} · {userInfo?.phone || ""} ·{" "}
                {userInfo?.realestateRegistrationNumber || ""} · {userInfo?.name || ""}
              </span>
                <span>
                {userInfo?.store || "Your Store"} · {userInfo?.address || ""} ·{" "}
                  {userInfo?.detailAddress || ""} · {userInfo?.phone || ""} ·{" "}
                  {userInfo?.realestateRegistrationNumber || ""} · {userInfo?.name || ""}
              </span>
              </TickerTrack>
            </Ticker>
          </BottomDock>
        </Stage>
      </Viewport>
  );
};

/* =========================
 * utils
 * ========================= */
function useNow(interval = 1000) {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), interval); return () => clearInterval(t); }, [interval]);
  return now;
}
function formatTime(d: Date) {
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}
function formatDate(d: Date) {
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const weekday = ["일","월","화","수","목","금","토"][d.getDay()];
  return `${y}.${m}.${day} (${weekday})`;
}

/* =========================
 * Styles (FULLSCREEN CONTAIN)
 * ========================= */
const Viewport = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden; /* 컨테인 스케일 시 남는 공간 배경으로 처리 */
  position: relative;
`;

const Stage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  /* width/height는 inline-style로 1920x1080 지정 */
  transform-origin: center center;
  overflow: hidden;
  animation: ${fadeIn} 300ms ease-out;
  background: var(--stage-gradient, ${DAY_THEME.stageGradient});
  color: var(--text-main, ${TEXT_MAIN});
  transition: background 0.6s ease, color 0.6s ease;
`;

/* Top HUD */
const TopHUD = styled.div`
  position: absolute; top: 14px; left: 14px; right: 14px; height: 65px;
  display: flex; align-items: center; justify-content: space-between;
`;
const Brand = styled.div` display: flex; align-items: baseline; gap: 12px; `;
const BrandName = styled.div` font-size: 34px; font-weight: 900; color: var(--text-main, ${TEXT_MAIN}); letter-spacing: 0.2px; `;
const BrandSub  = styled.div` font-size: 20px; font-weight: 700; color: var(--text-sub, ${TEXT_SUB}); `;
const Clock = styled.div` display: flex; flex-direction: row; align-items: end; gap: 14px; `;
const ClockTime = styled.div` font-size: 20px; font-weight: 900; color: var(--text-sub, ${TEXT_SUB}); `;
const ClockDate = styled.div` font-size: 30px; font-weight: 700; color: var(--text-main, ${TEXT_MAIN}); `;

/* Content Card */
const ContentCard = styled.div`
  position: absolute; top: 95px; left: 14px; right: 14px; bottom: 70px;
  background: var(--card-bg, ${CARD_BG}); border: 1px solid var(--card-border, ${CARD_BORDER});
  border-radius: 20px; overflow: hidden; backdrop-filter: blur(2px);
  box-shadow: var(--card-shadow, ${SHADOW});
`;
const SlideStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;
const SlideLayer = styled.div<{ $visible: boolean; $zIndex: number }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.6s ease;
  z-index: ${({ $zIndex }) => $zIndex};
  pointer-events: none;
`;
const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex !important;
  justify-content: center;
  align-items: stretch;
`;
const FullBox = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

/* Two & Six Layouts */
const TwoLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  height: 100%;
`;
const TwoTopRow = styled.div`
  display: flex;
  gap: 6px;
  flex: 30 1 0;
  min-height: 0;
`;
const TwoTopCard = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  border: 1px solid var(--card-border, ${CARD_BORDER});
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(18,45,90,0.08);
`;
const TwoBottomRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  flex: 16 1 0;
  min-height: 0;
`;
const TwoBottomCard = styled.div`
  display: flex;
  min-width: 0;
  border: 1px solid var(--card-border, ${CARD_BORDER});
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(18,45,90,0.08);
`;
const SixWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  height: 100%;
`;
const SixRow = styled.div`
  display: flex;
  gap: 6px;
  flex: 1;
  min-height: 0;
`;
const SixCard = styled.div`
  flex: 1;
  display: flex;
  min-width: 0;
  border: 1px solid var(--card-border, ${CARD_BORDER});
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(18,45,90,0.08);
`;

/* Bottom Ticker */
const BottomDock = styled.div`
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: 14px;
    height: 52px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    overflow: hidden;
    background: var(--ticker-bg, rgba(255,255,255,0.65));
`;
const Ticker = styled.div` width: 100%; height: 100%; position: relative; overflow: hidden; display: flex; align-items: center; `;
const TickerTrack = styled.div`
  display: inline-flex; white-space: nowrap; gap: 28px; width: max-content;
  animation: ${marquee} 46s linear infinite;
  font-size: 18px; font-weight: 700; color: var(--ticker-color, ${TEXT_MAIN}); letter-spacing: 0.15px;
  & > span { padding-right: 40px; opacity: 0.9; }
`;
const EmptySlide = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-main, ${TEXT_MAIN});
`;
